
import { ISelectedRow } from "./selected-row";
import { LookupApi } from "./LookupApi";
import { RestApi } from "./RestApi";
import { FormControl } from "react-bootstrap";
import { FormEvent } from "react";

export interface IRestResponse<T> {
    isSuccess   : boolean;
    message     : string | null;
    errors      : Record<string, string> | null;
    payload     : T | null;
}

export interface IRestModel {
    id      : number | string;
}

export type ValidationResult<T> = {
    isValid : boolean;
    key     : keyof T & string;
    message : string;
}

export type JsonInit                = Omit<RequestInit, 'body'> & { body?: unknown };
export type ValidationMap<T>        = Partial<Record<keyof T & string, (value: string) => ValidationResult<T>>>;
export type PropertyMap<T>          = Partial<Record<keyof T & string, (value: string) => unknown>>
export type SearchParams            = Record<string, string | number | boolean | null | undefined | Array<string | number | boolean | null | undefined>>;

export type ValidationCallback<T>   = () => ValidationMap<T>;
export type MapperCallback<T>       = () => PropertyMap<T>;

export type RestApiFormHooks<T extends IRestModel> = {
    useGetById      : (id: string | number | null) => IRestResponse<T> | undefined;
    useCreate       : () => {
        submit          : (payload: T) => Promise<IRestResponse<T>>;
        response        : IRestResponse<T> | undefined;
        error           : unknown;
        isMutating      : boolean;
    };
    useUpdate       : (id: string | number) => {
        submit          : (payload: T) => Promise<IRestResponse<T>>;
        response        : IRestResponse<T> | undefined;
        error           : unknown;
        isMutating      : boolean;
    };
    useDelete       : (id: string | number) => {
        submit          : () => Promise<IRestResponse<T>>;
        response        : IRestResponse<T> | undefined;
        error           : unknown;
        isMutating      : boolean;
    };
    refreshCachedEntity : (payload: T) => void;
}

export type RestApiListHooks<TSummary extends IRestModel>  = {
    useGetAll       : () => IRestResponse<TSummary[]> | undefined;
    useSearch       : (params: SearchParams | null) => IRestResponse<TSummary[]> | undefined;
};

export type RestApiHooks<TSummary extends IRestModel, TDetail extends IRestModel> = RestApiFormHooks<TDetail> & RestApiListHooks<TSummary> & {
    apiReference    : RestApi<TSummary,TDetail>;
};

export type LookupApiHooks<TData> = {
    apiReference    : LookupApi<TData>;
    useGetData      : () => IRestResponse<TData> | undefined;
    refresh         : (showSpinner?: (isVisible: boolean, message: string) => void) => Promise<IRestResponse<TData> | undefined>;
};

export type UseSearchReturn<TSearch, T extends IRestModel> = {
    params                      : TSearch & ISelectedRow;
    data                        : T[];
    isLoaded                    : boolean;
    message                     : string;
    selectedRow                 : T | null;
    equals                      : (val1 : unknown, val2 : unknown) => boolean;
    handleStringChange          : (e: React.FormEvent<unknown>) => void;
    handleIntChange             : (e: React.FormEvent<unknown>) => void;
    handleBoolChange            : (e: React.FormEvent<unknown>) => void;
    getDateHandler              : (format: string) => (e: React.FormEvent<unknown>) => void;
    getStringToggleGroupHandler : (fieldName: string) => (e: React.FormEvent<unknown>) => void;
    getIntToggleGroupHandler    : (fieldName: string) => (e: any) => void;
    setField                    : (key: keyof TSearch & string, value: string | number | boolean | null | undefined | Array<string | number | boolean | null | undefined>) => void;
    handleSearch                : () => void;
    refresh                     : () => void;
    selectRow                   : (id: string | number, rowData: T) => void;
    unselectRow                 : () => void;
};

export type UseFormReturn<T> = {
    data          : T;
    errors        : Record<keyof T & string, string>;
    isDirty       : boolean;
    isMutating    : boolean;
    isLoaded      : boolean;
    message       : string;

    handleStringChange          : (e: React.FormEvent<unknown>) => void;
    handleIntChange             : (e: React.FormEvent<unknown>) => void;
    handleBoolChange            : (e: React.FormEvent<unknown>) => void;
    getDateHandler              : (format: string) => (e: React.FormEvent<unknown>) => void;
    getStringToggleGroupHandler : (fieldName: string) => (e: React.FormEvent<unknown>) => void;
    getIntToggleGroupHandler    : (fieldName: string) => (e: React.FormEvent<unknown>) => void;
    handleBlur                  : (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
    handleSubmit                : (e: React.FormEvent) => Promise<boolean>;
    reset                       : () => void;
    replaceData                 : (next: T, options?: { baseline?: boolean; dirty?: boolean }) => void;

    setOnSaveComplete           : (cb: ((payload: T) => void) | null) => void;

    // escape hatches / advanced
    setField: (key: keyof T & string, value: string) => void;
    setError: (key: keyof T & string, message: string | null) => void;
};